import { useEffect, useState } from 'react';
import { Card, Grid, CircularProgress, Collapse, Icon, Select, MenuItem, Checkbox, ListItemText, OutlinedInput, InputLabel, FormControl } from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftInput from 'components/SoftInput';
import SoftButton from 'components/SoftButton';
import DataTableCustom from 'components/Tables/DataTableCustom';
import PaginatedExpandedRow from './PaginatedExpandedRow';
import { useQuery, gql, useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import 'dayjs/locale/es'; // Import Spanish locale
import { formatMoneyPunto } from 'utils/formatters';

dayjs.locale('es'); // Set locale to Spanish

const GET_MOVIMIENTOS_GENERALES = gql`
  query GetMovimientosGenerales($fechaInicio: String!, $fechaFin: String!, $conceptos: [String]) {
    movimientosGenerales(fechaInicio: $fechaInicio, fechaFin: $fechaFin, conceptos: $conceptos) {
      ingresos {
        mes
        total
        cantidad
        tipo
        medio
        items {
          fecha
          origen
          concepto
          medio
          monto
          observacion
        }
      }
      egresos {
        mes
        total
        cantidad
        tipo
        medio
        items {
          fecha
          concepto
          medio
          cuenta
          monto
          factura
        }
      }
      total
    }
  }
`;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const conceptosOptions = ['Cafetería', 'Comedor', 'Eventos'];

function ReporteFinanciero() {
    const [getReporteFinanciero, { data, loading, error }] = useLazyQuery(GET_MOVIMIENTOS_GENERALES);
    const [fechaInicio, setFechaInicio] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
    const [fechaFin, setFechaFin] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
    const [conceptos, setConceptos] = useState(['Cafetería']);

    const handleConceptoChange = (event) => {
        const {
            target: { value },
        } = event;
        setConceptos(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value,
        );
    };

    const handleFiltrar = () => {
        getReporteFinanciero({
            variables: {
                fechaInicio,
                fechaFin,
                conceptos,
            },
        });
    };

    useEffect(() => {
        handleFiltrar();
    }, []);

    // Columns for Main Ingresos Table
    const columnsIngresosMain = [
        {
            field: 'mesFormatted',
            headerName: 'Periodo',
            width: '20%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.mesFormatted}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'tipo',
            headerName: 'Concepto',
            width: '25%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.tipo}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'medio',
            headerName: 'Medio',
            width: '25%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.medio}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'total',
            headerName: 'Monto ($)',
            width: '20%',
            type: 'number',
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.total.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}</SoftTypography>
                </SoftBox>
            ),
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
        },
    ];

    // columns for Main Egresos Table
    const columnsEgresosMain = [
        {
            field: 'mesFormatted',
            headerName: 'Periodo',
            width: '15%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.mesFormatted}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'tipo',
            headerName: 'Tipo',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.tipo}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'medio',
            headerName: 'Medio',
            width: '25%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.medio}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'total',
            headerName: 'Monto ($)',
            width: '20%',
            type: 'number',
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.total.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}</SoftTypography>
                </SoftBox>
            ),
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
        },
    ];

    // Columns for Details Table
    const columnsIngresosDetails = [
        {
            field: 'fecha',
            headerName: 'Fecha',
            width: '10%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.fecha}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'origen',
            headerName: 'Origen',
            width: '10%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.origen}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'concepto',
            headerName: 'Concepto',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.concepto}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'medio',
            headerName: 'Medio',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.medio}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'monto',
            headerName: 'Monto ($)',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.monto.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'observacion',
            headerName: 'Observación',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.observacion}</SoftTypography>
                </SoftBox>
            )
        },
    ];

    const columnsEgresosDetails = [
        {
            field: 'fecha',
            headerName: 'Fecha',
            width: '10%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    {console.log('row.fecha', row.fecha)}
                    <SoftTypography variant="button" color="secondary">{row.fecha}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'concepto',
            headerName: 'Concepto',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.concepto}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'medio',
            headerName: 'Medio',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.medio}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'cuenta',
            headerName: 'Cuenta',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.cuenta}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'monto',
            headerName: 'Monto ($)',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.monto.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}</SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'factura',
            headerName: 'Factura',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    sx={{
                        width: "100%",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography
                            variant="caption"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="dark"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                            title={column.headerName}
                        >
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                opacity: 0.8
                            }}
                        >
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                opacity: 0.4,
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox sx={{ width: '100%' }}>
                    <SoftTypography variant="button" color="secondary">{row.factura}</SoftTypography>
                </SoftBox>
            )
        },
    ]

    if (loading) return <SoftBox display="flex" justifyContent="center" p={3}><CircularProgress /></SoftBox>;
    if (error) return (
        <Grid container textAlign={"center"} py={3}>
            <Grid item xs={12}>
                <Icon
                    sx={{
                        width: "50px",
                    }}
                >
                    warning
                </Icon>
            </Grid>
            <Grid item xs={12}>
                <SoftTypography variant="h6">Error al cargar los datos</SoftTypography>
            </Grid>
            <Grid item xs={12}>
                <SoftTypography variant="caption" component="p" color="text">
                    {error.message}
                </SoftTypography>
            </Grid>
        </Grid>
    );

    const ingresos = data?.movimientosGenerales?.ingresos || [];
    const ingresosData = ingresos.map((group, index) => ({
        id: `group-${index}`,
        mesFormatted: dayjs(group.mes).format('MMMM YYYY').toUpperCase(),
        ...group
    }));

    const egresos = data?.movimientosGenerales?.egresos || [];
    const egresosData = egresos.map((group, index) => ({
        id: `group-${index}`,
        mesFormatted: dayjs(group.mes).format('MMMM YYYY').toUpperCase(),
        ...group
    }));

    return (
        <SoftBox mt={2} p={2}>
            <SoftBox mb={3}>
                <SoftTypography variant="h5" fontWeight="bold" color="dark">
                    Reporte Financiero
                </SoftTypography>
            </SoftBox>

            {/* Filtros */}
            <SoftBox
                mb={3}
                p={2}
                sx={{
                    backgroundColor: '#f8f9fa',
                    borderRadius: '12px',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
            >
                <SoftTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                    Filtros
                </SoftTypography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <SoftTypography variant="caption" fontWeight="bold">Fecha Inicio</SoftTypography>
                        <SoftInput
                            type="date"
                            value={fechaInicio}
                            onChange={(e) => setFechaInicio(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <SoftTypography variant="caption" fontWeight="bold">Fecha Fin</SoftTypography>
                        <SoftInput
                            type="date"
                            value={fechaFin}
                            onChange={(e) => setFechaFin(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={3} mt={1}>
                        <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block">Concepto</SoftTypography>
                        <FormControl sx={{ width: '100%' }}>
                            <Select
                                multiple
                                displayEmpty
                                value={conceptos}
                                onChange={handleConceptoChange}
                                input={<OutlinedInput />}
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return <em>Todos</em>;
                                    }
                                    return selected.join(', ');
                                }}
                                MenuProps={MenuProps}
                                sx={{ height: '40px' }}
                            >
                                <MenuItem disabled value="">
                                    <em>Seleccionar Concepto</em>
                                </MenuItem>
                                {conceptosOptions.map((name) => (
                                    <MenuItem key={name} value={name}>
                                        <SoftBox display="flex" width="100%" alignItems="center" gap={1}>
                                            <Checkbox checked={conceptos.indexOf(name) > -1} />
                                            <SoftTypography variant="button2" fontWeight="bold" mt={.5}>{name}</SoftTypography>
                                        </SoftBox>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <SoftBox mt={3}>
                            <SoftButton variant="gradient" color="info" onClick={handleFiltrar} fullWidth>
                                Filtrar
                            </SoftButton>
                        </SoftBox>
                    </Grid>
                </Grid>
            </SoftBox>

            {/* Ingresos Section */}
            <SoftBox mb={4}>
                <SoftTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                    INGRESOS
                </SoftTypography>
                <DataTableCustom
                    columns={columnsIngresosMain}
                    data={ingresosData}
                    expandedRowRender={(row) => (
                        <PaginatedExpandedRow
                            items={row.items}
                            columns={columnsIngresosDetails}
                            rowId={row.id}
                            pageSize={50}
                        />
                    )}
                    rowExpandable={() => true}
                    showActions={false}
                    zebraStripes={true}
                    border={true}
                    headerStyles={{
                        backgroundColor: '#fff',
                        borderBottom: '2px solid #e0e0e0',
                        color: '#344767',
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                    }}
                    footerContent={
                        <SoftBox display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            <SoftTypography variant="caption" color="text">
                                Total registros: {ingresosData?.length || 0}
                            </SoftTypography>
                            <SoftTypography variant="h6" color="secondary" fontWeight="regular">
                                Total ingresos <strong>${formatMoneyPunto(ingresosData.reduce((total, item) => total + item.total, 0))}</strong>
                            </SoftTypography>
                        </SoftBox>
                    }
                />
            </SoftBox>

            {/* Egresos Section */}
            <SoftBox mb={4}>
                <SoftTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                    EGRESOS
                </SoftTypography>
                <DataTableCustom
                    columns={columnsEgresosMain}
                    data={egresosData}
                    expandedRowRender={(row) => (
                        <PaginatedExpandedRow
                            items={row.items}
                            columns={columnsEgresosDetails}
                            rowId={row.id}
                            pageSize={50}
                        />
                    )}
                    rowExpandable={() => true}
                    showActions={false}
                    zebraStripes={true}
                    border={true}
                    headerStyles={{
                        backgroundColor: '#fff',
                        borderBottom: '2px solid #e0e0e0',
                        color: '#344767',
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                    }}
                    footerContent={
                        <SoftBox display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            <SoftTypography variant="caption" color="text">
                                Total registros: {egresosData?.length || 0}
                            </SoftTypography>
                            <SoftTypography variant="h6" color="secondary" fontWeight="regular">
                                Total egresos <strong>${formatMoneyPunto(egresosData.reduce((total, item) => total + item.total, 0))}</strong>
                            </SoftTypography>
                        </SoftBox>
                    }
                />
            </SoftBox>
            <Card sx={{ mb: 2 }}>
                <SoftBox display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={2}>
                    <SoftTypography variant="h6" color="text">
                        Total
                    </SoftTypography>
                    <SoftTypography
                        variant="h4"
                        sx={{ color: data?.movimientosGenerales.total >= 0 ? "green" : "red" }}
                    >
                        ${formatMoneyPunto(data?.movimientosGenerales.total)}
                    </SoftTypography>
                </SoftBox>
            </Card>
        </SoftBox>
    );
}

export default ReporteFinanciero;
